import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Aperture, Play } from "lucide-react";
import { PROJECTS, FILTERS, GROUP_ORDER } from "../data/site";

const Tile = ({ project }) => (
  <motion.article
    layout
    initial={{ opacity: 0, scale: 0.97 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.97 }}
    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`project-tile-${project.id}`}
    className="group relative bg-black overflow-hidden cursor-zoom-in aspect-[4/5] ring-1 ring-[#222]"
    onClick={() => window.dispatchEvent(new CustomEvent("open-lightbox", { detail: project }))}
    onMouseMove={(e) => {
      const r = e.currentTarget.getBoundingClientRect();
      e.currentTarget.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
      e.currentTarget.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
    }}
  >
    {project.thumb ? (
      <img
        src={project.thumb}
        alt={project.title}
        loading="lazy"
        draggable={false}
        className={`magnet absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out ${project.focus || "object-center"}`}
      />
    ) : (
      <div className="absolute inset-0 bg-[#111111] noise overflow-hidden flex flex-col items-center justify-center gap-5 px-8 text-center">
        <span aria-hidden="true" className="absolute font-syncopate font-bold leading-none text-[8rem] md:text-[10rem] text-outline-dim select-none">
          {project.index}
        </span>
        <Aperture size={34} className="relative text-[#4A4A4A] group-hover:text-white transition-colors duration-500" />
        <p className="relative font-jetmono text-[10px] leading-relaxed tracking-[0.15em] uppercase text-[#808080] max-w-[26ch]">
          [ {project.thumbLabel} ]
        </p>
      </div>
    )}

    {/* HOVER STATE VIDEO LOOP SLOT — set project.hoverVideo to a real mp4 URL */}
    <video
      data-testid={`project-video-${project.id}`}
      data-media-slot="hover-video"
      src={project.hoverVideo || undefined}
      autoPlay
      muted
      loop
      playsInline
      preload="none"
      disablePictureInPicture
      controlsList="nodownload nofullscreen noremoteplayback"
      className="magnet absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-[opacity,transform] duration-700"
    />

    <div className="pointer-events-none absolute inset-0 bg-black opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
    <div className="pointer-events-none absolute inset-0 spotlight opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

    <span className="absolute top-4 right-4 font-jetmono text-[10px] tracking-[0.2em] text-[#808080] group-hover:text-white transition-colors duration-500">
      {project.index}
    </span>

    {!project.hoverVideo && !project.thumb && (
      <span className="absolute top-4 left-4 flex items-center gap-2 font-jetmono text-[9px] tracking-[0.2em] uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <Play size={10} className="animate-rec" />
        [ {project.hoverLabel} ]
      </span>
    )}

    <div className="pointer-events-none absolute bottom-0 left-0 right-0 p-5 md:p-6 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500 ease-out max-md:hidden">
      <p className="font-jetmono text-[10px] tracking-[0.25em] uppercase text-[#808080] mb-2">
        {project.category} — {project.role}
      </p>
      <h3 className="font-syncopate font-bold uppercase text-lg md:text-xl leading-tight text-white">
        {project.title}
      </h3>
    </div>

    <div className="pointer-events-none absolute bottom-0 left-0 right-0 p-4 pt-10 md:hidden bg-gradient-to-t from-black/85 to-transparent">
      <p className="font-jetmono text-[9px] tracking-[0.25em] uppercase text-[#808080] mb-1">{project.category}</p>
      <h3 className="font-syncopate font-bold uppercase text-sm leading-tight text-white">{project.title}</h3>
    </div>
  </motion.article>
);

export const WorkGrid = () => {
  const [filter, setFilter] = useState("All");
  const visible = (filter === "All"
    ? [...PROJECTS].sort((a, b) => (GROUP_ORDER[a.category] ?? 9) - (GROUP_ORDER[b.category] ?? 9))
    : PROJECTS.filter((p) => p.category === filter)
  ).map((p, i) => ({ ...p, index: String(i + 1).padStart(2, "0") }));

  return (
    <section id="work" data-testid="work-section" className="bg-black px-4 md:px-10 py-20 md:py-28">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-10 md:mb-14"
      >
        <p className="font-jetmono text-[10px] tracking-[0.35em] uppercase text-[#808080] mb-4">01 / SELECTED WORK</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <h2 className="font-syncopate font-bold uppercase leading-none tracking-tight text-4xl sm:text-6xl lg:text-7xl">
            THE WORK<span className="text-outline-dim">.</span>
          </h2>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter projects">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-btn-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`font-jetmono text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${
                  filter === f
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-[#808080] border-[#333] hover:border-white hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px">
        <AnimatePresence mode="popLayout">
          {visible.map((p) => (
            <Tile key={p.id} project={p} />
          ))}
        </AnimatePresence>
      </motion.div>

      <p className="mt-6 font-jetmono text-[10px] tracking-[0.2em] uppercase text-[#555]" data-testid="work-count">
        SHOWING {visible.length} / {PROJECTS.length} PROJECTS
      </p>
    </section>
  );
};
